import type { KkmServerResponse } from '@/shared/api';
import type { OrderFiscalDriveDocumentInput } from '@shared/api/graphql/generated';

import { isRecord, optionalPositiveInteger, optionalString } from '@/shared/api';
import { OrderFiscalOperationType } from '@shared/api/graphql/generated';

import { extractKkmFiscalDocumentDraft, parseKkmFiscalQrCode } from './kkm-fiscal-document-parser';

export interface KkmFiscalDriveReader {
  getDataCheck(deviceId: string, fiscalNumber: number): Promise<KkmServerResponse | null>;
}

/**
 * Сколько последних документов ФН читаем за раз. Если номер начала чтения старше,
 * чтение не делается: «чека нет» по обрезанному окну дало бы второй чек к уже пробитому.
 */
export const MAX_FISCAL_DRIVE_DOCUMENTS = 50;

/**
 * `RegisterCheck.CheckType` ответа `GetDataCheck` для чеков, которые пробивает Mors,
 * и признак расчёта `n` в QR того же чека.
 */
const RECEIPTS: Readonly<Record<string, { operationType: OrderFiscalOperationType; qrOperation: number }>> = {
  Приход: { operationType: OrderFiscalOperationType.Settlement, qrOperation: 1 },
  'Возврат прихода': { operationType: OrderFiscalOperationType.Refund, qrOperation: 2 },
};

/**
 * Документы ФН, которые не могут быть чеком операции Mors. Коррекция, чек без признака
 * расчёта, пустой или незнакомый тип сюда не входят: по ним «чека нет» не доказать.
 */
const FOREIGN_DOCUMENTS: ReadonlySet<string> = new Set([
  'Расход',
  'Возврат расхода',
  'Открытие смены',
  'Закрытие смены',
  'Текущее состояние расчетов',
  'Отчет о регистрации',
  'Отчет об изменении параметров регистрации',
  'Отчет о закрытии фискального накопителя',
  'Подтверждение оператора',
]);

/**
 * Читает документы фискального накопителя с номерами ФД от `fromFd` до последнего.
 * Отчёты смены и чужие чеки пропускаются. Коррекция, нераспознанный ответ или сбой
 * чтения посередине дают `null`: «не прочитано», а не «чека нет».
 */
export async function readFiscalDocuments(
  reader: KkmFiscalDriveReader,
  deviceId: string,
  fromFd: number,
  timezone: string,
): Promise<OrderFiscalDriveDocumentInput[] | null> {
  const last = await reader.getDataCheck(deviceId, 0);
  const lastNumber = last === null ? null : headerOf(last)?.fiscalNumber;

  if (lastNumber == null) {
    return null;
  }

  // Чек операции может лежать до окна: исход решает человек, а не повтор печати.
  if (fromFd < lastNumber - MAX_FISCAL_DRIVE_DOCUMENTS + 1) {
    return null;
  }

  const documents: OrderFiscalDriveDocumentInput[] = [];
  const firstNumber = Math.max(fromFd, 1);

  for (let number = firstNumber; number <= lastNumber; number += 1) {
    const response = number === lastNumber ? last : await reader.getDataCheck(deviceId, number);
    const document = response === null ? undefined : toDocument(response, number, timezone);

    if (document === undefined) {
      return null;
    }

    if (document !== null) {
      documents.push(document);
    }
  }

  return documents;
}

/**
 * `undefined` — по документу нельзя решить, чек ли это операции; `null` — не чек Mors.
 * Номера чека и смены `GetDataCheck` не отдаёт, реквизиты чека берутся из QR.
 */
function toDocument(
  response: KkmServerResponse,
  number: number,
  timezone: string,
): OrderFiscalDriveDocumentInput | null | undefined {
  const header = headerOf(response);

  if (header?.fiscalNumber !== number) {
    return undefined;
  }

  if (header.checkType !== null && FOREIGN_DOCUMENTS.has(header.checkType)) {
    return null;
  }

  const receipt = header.checkType === null ? undefined : RECEIPTS[header.checkType];
  const qr = receipt === undefined ? null : qrOf(response, timezone);

  if (
    receipt === undefined ||
    qr?.fiscalDocumentNumber !== number ||
    qr.totalAmount === null ||
    qr.operationType !== receipt.qrOperation
  ) {
    return undefined;
  }

  return {
    fiscal: extractKkmFiscalDocumentDraft(response, timezone),
    operationType: receipt.operationType,
    totalAmount: toKopecks(qr.totalAmount),
  };
}

/** Номер ФД и тип документа: только объект `RegisterCheck` ответа `GetDataCheck`. */
function headerOf(response: KkmServerResponse): { checkType: string | null; fiscalNumber: number | null } | null {
  const check = response.raw['RegisterCheck'];

  return isRecord(check)
    ? { checkType: optionalString(check['CheckType']), fiscalNumber: optionalPositiveInteger(check['FiscalNumber']) }
    : null;
}

function qrOf(response: KkmServerResponse, timezone: string): ReturnType<typeof parseKkmFiscalQrCode> {
  const raw = response.raw['QRCode'];

  return typeof raw === 'string' ? parseKkmFiscalQrCode(raw, timezone) : null;
}

function toKopecks(amount: string): number {
  const [rubles = '0', kopecks = ''] = amount.split('.');

  return Number(rubles) * 100 + Number(kopecks.padEnd(2, '0'));
}
